import keywordStyles, {
  blockKeywords,
  specialKeywords,
  type KeywordStyles,
} from "features/QuestionnaireEditor/components/Editor/Extensions/keywordStyles"
import { Token } from "features/Docs/types/docs.types"

/**
 * Static, pure highlighter for DATA(Q) questionnaire snippets shown in docs.
 *
 * Ports the per-line logic of the questionnaire editor's FontChangeExtension
 * (comments, `Q`/`QUESTION` three-part colouring, line-leading keyword +
 * content, block-keyword continuation, mid-line special keywords, `[[…]]`
 * and `<<…>>` spans) into a `(code) => Token[][]` function — one Token array
 * per line — reusing the same global `highlight-*` classes as the editor.
 */

// Docs-only template keyword additions. Docs syntax templates use placeholder
// lines like `FILL IN <NUMBER | REAL | TEXT>` — a bare `FILL IN` followed by a
// choice placeholder rather than a concrete type. The shared keywordStyles only
// defines the concrete three-word forms (`FILL IN NUMBER`/`REAL`/`TEXT`), so
// these template lines match no leading keyword and get mis-painted as
// block-keyword continuations. We add bare `FILL IN`/`FILLIN` here — deliberately
// NOT to the shared keywordStyles — so the live questionnaire editor's keyword
// set (and its behaviour) stays untouched. The classes mirror the real
// `FILL IN TEXT` entry (highlight-bold keyword, highlight-item content).
const DOCS_TEMPLATE_KEYWORDS: KeywordStyles = {
  "FILL IN": { keywordClass: "highlight-bold", contentClass: "highlight-item" },
  FILLIN: { keywordClass: "highlight-bold", contentClass: "highlight-item" },
}

// General-keyword styles used by the docs highlighter: the shared editor keywords
// plus the docs-only template keys above.
const GENERAL_KEYWORD_STYLES: KeywordStyles = {
  ...keywordStyles,
  ...DOCS_TEMPLATE_KEYWORDS,
}

// General keywords: longest by word-count then character length first, so
// multi-word keys ("SELECT ONE FROM") win over their prefixes ("SELECT"), and
// the concrete three-word `FILL IN NUMBER`/`REAL`/`TEXT` still win over the
// two-word docs `FILL IN` on real lines. Q/QUESTION are handled separately.
const GENERAL_KEYWORDS = Object.keys(GENERAL_KEYWORD_STYLES)
  .filter((keyword) => keyword !== "Q" && keyword !== "QUESTION")
  .sort(
    (a, b) => b.split(" ").length - a.split(" ").length || b.length - a.length,
  )

const BLOCK_KEYWORDS = Object.keys(blockKeywords).sort(
  (a, b) => b.length - a.length,
)

// Special keywords whose mid-line highlight is suppressed at the line start.
const SKIP_AT_START = new Set([
  "IN",
  "NOT",
  "ANY OF",
  "AND",
  "OR",
  "AS",
  "IS",
  "IS NOT",
  "WHERE",
  "COUNT",
])

const isWordChar = (char: string | undefined): boolean =>
  char !== undefined && /\w/.test(char)

/** True when `trimmed` starts with `keyword` on a word-ish boundary. */
const leadsWith = (trimmed: string, keyword: string): boolean =>
  trimmed.startsWith(keyword) && !isWordChar(trimmed[keyword.length])

/** Paint class over [start, end) of the per-character class map. */
const paint = (
  classes: (string | undefined)[],
  start: number,
  end: number,
  className: string,
): void => {
  for (let i = Math.max(0, start); i < Math.min(classes.length, end); i++) {
    classes[i] = className
  }
}

/** Coalesce the per-character class map into runs of Tokens. */
const coalesce = (line: string, classes: (string | undefined)[]): Token[] => {
  if (line.length === 0) return [{ text: "" }]
  const tokens: Token[] = []
  let start = 0
  for (let i = 1; i <= line.length; i++) {
    if (i === line.length || classes[i] !== classes[start]) {
      const className = classes[start]
      const text = line.slice(start, i)
      tokens.push(className ? { text, className } : { text })
      start = i
    }
  }
  return tokens
}

/** Ranges (in `line`) covered by `<<…>>` variable references. */
const angleRanges = (line: string): Array<[number, number]> => {
  const ranges: Array<[number, number]> = []
  const regex = /<<[^>]*?>>/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(line)) !== null) {
    ranges.push([match.index, match.index + match[0].length])
  }
  return ranges
}

const insideAny = (
  ranges: Array<[number, number]>,
  start: number,
  end: number,
): boolean => ranges.some(([s, e]) => start >= s && end <= e)

/** Paint mid-line special keywords that sit outside `<<…>>` references. */
const paintSpecialKeywords = (
  line: string,
  classes: (string | undefined)[],
): void => {
  const ranges = angleRanges(line)
  const contentStart = line.length - line.trimStart().length
  specialKeywords.forEach((keyword) => {
    const regex = new RegExp(`\\b${keyword}(?=\\s|$|\\()`, "g")
    let match: RegExpExecArray | null
    while ((match = regex.exec(line)) !== null) {
      const start = match.index
      const end = start + keyword.length
      if (SKIP_AT_START.has(keyword) && start === contentStart) continue
      if (insideAny(ranges, start, end)) continue
      paint(classes, start, end, keywordStyles[keyword]?.keywordClass || "highlight-as")
    }
  })
}

const paintDelimited = (
  line: string,
  classes: (string | undefined)[],
  regex: RegExp,
  className: string,
): void => {
  regex.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = regex.exec(line)) !== null) {
    paint(classes, match.index, match.index + match[0].length, className)
  }
}

const RECODE_TARGET =
  /^(\s*(?:RECODE\s+)?)(<<[^<>]+?>>|[A-Za-z_]\w*)(\s*::\s*<<[^<>]+?>>)?(?=\s*=(?!=))/

/** Overlay inline decorations (special keywords, `<<…>>`, `[[…]]`, RECODE target). */
const applyInlineOverrides = (
  line: string,
  classes: (string | undefined)[],
  skipSpecial: boolean,
): void => {
  if (!skipSpecial) paintSpecialKeywords(line, classes)

  const recode = RECODE_TARGET.exec(line)
  if (recode && recode[2]) {
    const start = recode[1].length
    paint(classes, start, start + recode[2].length, "highlight-validate")
  }

  paintDelimited(line, classes, /<<[^>]*?>>/g, "highlight-validate")
  paintDelimited(line, classes, /\[\[[\s\S]*?\]\]/g, "highlight-validate")
}

/**
 * Highlight a block of DATA(Q) questionnaire text.
 * @returns one Token array per line (concatenating token text rebuilds the line).
 */
export const highlightDataq = (code: string): Token[][] => {
  let insideBlock: string | null = null
  let emptyLineCount = 0

  return code.split(/\r?\n/).map((line) => {
    const trimmed = line.trim()
    const classes: (string | undefined)[] = new Array(line.length).fill(
      undefined,
    )

    // Comment / remark line — does not affect block state or blank counting.
    if (trimmed.startsWith("#") || trimmed.startsWith("REMARK")) {
      paint(classes, 0, line.length, "highlight-remark")
      return coalesce(line, classes)
    }

    // Blank lines: a single one is allowed inside a block (matching the
    // editor's threshold of 1); a second consecutive one ends the block.
    if (trimmed === "") {
      emptyLineCount++
      if (emptyLineCount > 1) insideBlock = null
      return coalesce(line, classes)
    }
    emptyLineCount = 0

    const blockBefore = insideBlock
    const leadingWhitespace = line.length - line.trimStart().length

    // Q / QUESTION three-part colouring (keyword / label / colon + text).
    const qMatch = /^(\s*)(Q|QUESTION)(\s+[^:]*)(:[\s\S]*)?$/.exec(line)
    if (qMatch) {
      const [, ws, keyword, label, rest] = qMatch
      const keywordStart = ws.length
      const keywordEnd = keywordStart + keyword.length
      paint(classes, keywordStart, keywordEnd, keywordStyles[keyword].keywordClass)
      const labelEnd = keywordEnd + label.length
      paint(classes, keywordEnd, labelEnd, "highlight-item")
      if (rest) paint(classes, labelEnd, line.length, "highlight-text-black")
      insideBlock = "highlight-text-black"
      applyInlineOverrides(line, classes, false)
      return coalesce(line, classes)
    }

    const blockKeyword = BLOCK_KEYWORDS.find((keyword) => leadsWith(trimmed, keyword))
    const generalKeyword = GENERAL_KEYWORDS.find((keyword) => leadsWith(trimmed, keyword))
    let hasLeading = false

    if (generalKeyword) {
      const { keywordClass, contentClass } = GENERAL_KEYWORD_STYLES[generalKeyword]
      const start = leadingWhitespace
      const end = start + generalKeyword.length
      paint(classes, start, end, keywordClass)
      if (contentClass) paint(classes, end, line.length, contentClass)
      hasLeading = true
    }

    if (blockKeyword) {
      const start = leadingWhitespace
      const end = start + blockKeyword.length
      const blockClass = blockKeywords[blockKeyword]
      if (blockKeyword === "NOTE" || blockKeyword === "TEXT") {
        paint(classes, start, end, "highlight-bold")
        const after = line.slice(end)
        const labelColon =
          blockKeyword === "TEXT" ? /^(\s+[^:]*?)(:[\s\S]*)$/.exec(after) : null
        if (labelColon) {
          const labelEnd = end + labelColon[1].length
          paint(classes, end, labelEnd, "highlight-item")
          paint(classes, labelEnd, line.length, blockClass)
        } else {
          paint(classes, end, line.length, blockClass)
        }
      } else {
        paint(classes, start, end, "highlight-bold")
        paint(classes, end, line.length, blockClass)
      }
      insideBlock = blockClass
      hasLeading = true
    } else if (generalKeyword) {
      // A non-block leading keyword ends any open block.
      insideBlock = null
    }

    if (!hasLeading && blockBefore) {
      // Continuation line inside an open block: inherit the block's colour.
      paint(classes, 0, line.length, blockBefore)
    }

    if (!hasLeading && !blockBefore) {
      // Stray line: no leading keyword and outside any open block. In docs such
      // lines are pseudo-syntax notation (e.g. `SELECT [ONE|MULTIPLE] FROM
      // [ListName] IF SELECTED [SourceQuestionLabel]`), so they render fully
      // plain — all or nothing. We skip every inline overlay (special keywords,
      // `<<…>>`, `[[…]]`, RECODE target); otherwise mid-line words like
      // `IF SELECTED` / `IF NOT` would paint over an otherwise-plain line and
      // produce a half-highlighted look. This differs from the questionnaire
      // editor, which flags stray lines red as live "unrecognised line" feedback.
      return coalesce(line, classes)
    }

    const inTextBlock =
      (blockBefore === "highlight-note" ||
        blockBefore === "highlight-text-black") &&
      !hasLeading
    applyInlineOverrides(line, classes, inTextBlock)

    return coalesce(line, classes)
  })
}

export default highlightDataq
